/**
 * OAuth Token Manager
 *
 * Proactively manages Google OAuth tokens to prevent expiration issues.
 * This service runs in the background and:
 * - Checks token health periodically
 * - Proactively refreshes tokens before they expire
 * - Notifies the UI when reconnection is needed
 * - Handles testing mode 7-day refresh token limits
 */

import { getAccessToken } from '../lib/supabaseRest';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Refresh tokens 30 minutes before they expire
const PROACTIVE_REFRESH_BUFFER_MINUTES = 30;
// Check token health every 5 minutes
const HEALTH_CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Warn user when refresh token has 2 days or less remaining
const REFRESH_TOKEN_WARNING_DAYS = 2;

export interface TokenHealth {
  connected: boolean;
  isExpired: boolean;
  accessTokenExpiresIn: number; // minutes
  refreshTokenDaysRemaining: number | null;
  needsReconnect: boolean;
  lastRefreshed: string | null;
  refreshCount: number;
  oauthAppStatus: 'testing' | 'production';
  error?: string;
}

export interface TokenStatus {
  gmail: TokenHealth | null;
  calendar: TokenHealth | null;
  lastChecked: Date;
}

type TokenWarningCallback = (service: 'gmail' | 'calendar', daysRemaining: number) => void;
type ReconnectCallback = (service: 'gmail' | 'calendar') => void;

class OAuthTokenManager {
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private tokenStatus: TokenStatus = {
    gmail: null,
    calendar: null,
    lastChecked: new Date(0),
  };
  private warningCallbacks: Set<TokenWarningCallback> = new Set();
  private reconnectCallbacks: Set<ReconnectCallback> = new Set();
  private isRunning = false;

  /**
   * Get authorization header for Edge Function calls
   * CRITICAL FIX: Now async to await token refresh if needed
   */
  private async getAuthHeader(): Promise<string | null> {
    const accessToken = await getAccessToken();
    if (!accessToken) return null;
    return `Bearer ${accessToken}`;
  }

  /**
   * Make authenticated request to Edge Function
   * CRITICAL FIX: Now awaits async getAuthHeader()
   */
  private async edgeFetch(
    functionName: string,
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response | null> {
    const authHeader = await this.getAuthHeader();
    if (!authHeader) return null;

    try {
      const url = `${SUPABASE_URL}/functions/v1/${functionName}${endpoint}`;
      return await fetch(url, {
        ...options,
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
    } catch (error) {
      console.error(`[OAuthTokenManager] Failed to call ${functionName}${endpoint}:`, error);
      return null;
    }
  }

  /**
   * Check health of a specific OAuth connection
   */
  async checkHealth(service: 'gmail' | 'calendar'): Promise<TokenHealth | null> {
    const functionName = service === 'gmail' ? 'gmail-oauth' : 'calendar-oauth';
    const response = await this.edgeFetch(functionName, '/health');

    if (!response) return null;

    try {
      const health: TokenHealth = await response.json();
      return health;
    } catch {
      return null;
    }
  }

  /**
   * Proactively refresh a token if needed
   */
  async proactiveRefresh(service: 'gmail' | 'calendar'): Promise<boolean> {
    const functionName = service === 'gmail' ? 'gmail-oauth' : 'calendar-oauth';
    const response = await this.edgeFetch(functionName, '/proactive-refresh', {
      method: 'POST',
    });

    if (!response) return false;

    try {
      const result = await response.json();
      if (result.refreshed) {
        console.log(`[OAuthTokenManager] Proactively refreshed ${service} token`);
      }
      return result.success;
    } catch {
      return false;
    }
  }

  /**
   * Check all OAuth connections and refresh if needed
   */
  async checkAndRefreshAll(): Promise<TokenStatus> {
    console.log('[OAuthTokenManager] Running health check...');

    // Check Gmail
    const gmailHealth = await this.checkHealth('gmail');
    if (gmailHealth?.connected && !gmailHealth.needsReconnect) {
      // Check if we should proactively refresh
      if (gmailHealth.accessTokenExpiresIn < PROACTIVE_REFRESH_BUFFER_MINUTES) {
        await this.proactiveRefresh('gmail');
        // Re-check health after refresh
        this.tokenStatus.gmail = await this.checkHealth('gmail');
      } else {
        this.tokenStatus.gmail = gmailHealth;
      }

      // Check for refresh token expiry warning
      if (gmailHealth.refreshTokenDaysRemaining !== null &&
          gmailHealth.refreshTokenDaysRemaining <= REFRESH_TOKEN_WARNING_DAYS) {
        this.notifyWarning('gmail', gmailHealth.refreshTokenDaysRemaining);
      }
    } else {
      this.tokenStatus.gmail = gmailHealth;
      if (gmailHealth?.needsReconnect) {
        this.notifyReconnectNeeded('gmail');
      }
    }

    // Check Calendar
    const calendarHealth = await this.checkHealth('calendar');
    if (calendarHealth?.connected && !calendarHealth.needsReconnect) {
      if (calendarHealth.accessTokenExpiresIn < PROACTIVE_REFRESH_BUFFER_MINUTES) {
        await this.proactiveRefresh('calendar');
        this.tokenStatus.calendar = await this.checkHealth('calendar');
      } else {
        this.tokenStatus.calendar = calendarHealth;
      }

      if (calendarHealth.refreshTokenDaysRemaining !== null &&
          calendarHealth.refreshTokenDaysRemaining <= REFRESH_TOKEN_WARNING_DAYS) {
        this.notifyWarning('calendar', calendarHealth.refreshTokenDaysRemaining);
      }
    } else {
      this.tokenStatus.calendar = calendarHealth;
      if (calendarHealth?.needsReconnect) {
        this.notifyReconnectNeeded('calendar');
      }
    }

    this.tokenStatus.lastChecked = new Date();
    return this.tokenStatus;
  }

  /**
   * Notify listeners about refresh token expiry warning
   */
  private notifyWarning(service: 'gmail' | 'calendar', daysRemaining: number): void {
    this.warningCallbacks.forEach(callback => {
      try {
        callback(service, daysRemaining);
      } catch (error) {
        console.error('[OAuthTokenManager] Warning callback error:', error);
      }
    });
  }

  /**
   * Notify listeners that reconnection is needed
   */
  private notifyReconnectNeeded(service: 'gmail' | 'calendar'): void {
    this.reconnectCallbacks.forEach(callback => {
      try {
        callback(service);
      } catch (error) {
        console.error('[OAuthTokenManager] Reconnect callback error:', error);
      }
    });
  }

  /**
   * Subscribe to token expiry warnings
   */
  onWarning(callback: TokenWarningCallback): () => void {
    this.warningCallbacks.add(callback);
    return () => this.warningCallbacks.delete(callback);
  }

  /**
   * Subscribe to reconnection needed events
   */
  onReconnectNeeded(callback: ReconnectCallback): () => void {
    this.reconnectCallbacks.add(callback);
    return () => this.reconnectCallbacks.delete(callback);
  }

  /**
   * Get current token status (cached)
   */
  getStatus(): TokenStatus {
    return this.tokenStatus;
  }

  /**
   * Start the background health check service
   */
  start(): void {
    if (this.isRunning) return;

    console.log('[OAuthTokenManager] Starting background health check service');
    this.isRunning = true;

    // Run initial check
    this.checkAndRefreshAll();

    // Set up periodic checks
    this.healthCheckInterval = setInterval(() => {
      this.checkAndRefreshAll();
    }, HEALTH_CHECK_INTERVAL_MS);
  }

  /**
   * Stop the background health check service
   */
  stop(): void {
    if (!this.isRunning) return;

    console.log('[OAuthTokenManager] Stopping background health check service');
    this.isRunning = false;

    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  /**
   * Check if the service is running
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * Force an immediate health check and refresh
   */
  async forceCheck(): Promise<TokenStatus> {
    return this.checkAndRefreshAll();
  }
}

// Export singleton instance
export const oauthTokenManager = new OAuthTokenManager();
